"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import * as s from "@/lib/styles";
import { RequireAuth } from "@/components/require-auth";
import { apiGet, apiDelete, apiPost, apiPut, apiUploadForm } from "@/lib/api";
import { TrashIcon, EditIcon, Spinner, Download, RefreshIcon, ImportIcon, SparklesIcon } from "@/lib/icons";
import { useToast } from "@/components/toast-provider";
import { useDocumentSet } from "@/lib/document-set-context";
import ConfirmDialog from "@/components/confirm-dialog";

interface ScanListItem {
  id: string;
  uploadedBy: string;
  createdAt: string;
  pageCount: number;
  thumbnail?: string | null;
  preview: string;
  /** Full OCR text (searched by the filter). */
  ocrText?: string | null;
  summary?: string | null;
  locationCode?: string | null;
  /** Set the document lives in — shown as a badge in the "All" view. */
  setName?: string;
}

interface ScanDetail {
  id: string;
  ocrText: string;
  summary?: string | null;
  locationCode?: string | null;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function downloadScan(id: string) {
  // Cookie auth is sent same-origin; server returns the PDF inline, so this
  // opens it in the browser's PDF viewer (Safari Preview, etc.) in a new tab.
  window.open(`/api/scans/${encodeURIComponent(id)}/download`, "_blank", "noopener");
}

/** Increment the trailing number of a location code, preserving its digit width.
 *  e.g. "NC-A-002" → "NC-A-003", "AB-099" → "AB-100". Returns "" if none. */
function incrementLocation(code: string): string {
  const m = code.trim().match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return "";
  const [, prefix, digits, suffix] = m;
  const next = (parseInt(digits, 10) + 1).toString().padStart(digits.length, "0");
  return `${prefix}${next}${suffix}`;
}

export default function DocumentsPage() {
  const { showToast } = useToast();
  const { current: documentSet, isAll } = useDocumentSet();
  const [items, setItems] = useState<ScanListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<ScanListItem | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [aiBusy, setAiBusy] = useState<Set<string>>(new Set());
  /** Location code drafts (while editing or first entry). */
  const [locDrafts, setLocDrafts] = useState<Record<string, string>>({});
  /** Doc ids currently unlocked for location edit (or never set yet is always editable). */
  const [locEditing, setLocEditing] = useState<Set<string>>(new Set());
  const [locSaving, setLocSaving] = useState<Set<string>>(new Set());
  /** Most recently used location code — the next suggestion increments it. */
  const [lastLocation, setLastLocation] = useState("");
  /** Last value known to be persisted (or loaded) — used to skip no-op saves. */
  const savedRef = useRef<Record<string, string>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGet<ScanListItem[]>("/scans")
      .then((list) => {
        setItems(list);
        const next: Record<string, string> = {};
        const locs: Record<string, string> = {};
        for (const d of list) {
          next[d.id] = d.summary ?? "";
          locs[d.id] = d.locationCode ?? "";
        }
        setDrafts(next);
        setLocDrafts(locs);
        savedRef.current = { ...next };
        setLocEditing(new Set());
        // Seed the suggestion from the most-recently-created doc that has a
        // location (list is returned newest-first).
        const seed = list.find((d) => (d.locationCode ?? "").trim())?.locationCode?.trim() ?? "";
        setLastLocation(seed);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load documents"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    return () => {
      for (const t of Object.values(timersRef.current)) clearTimeout(t);
    };
  }, [load]);

  const persistSummary = useCallback(
    async (id: string, value: string) => {
      const summary = value.trim();
      const previously = (savedRef.current[id] ?? "").trim();
      if (summary === previously) return;

      setSaving((prev) => new Set(prev).add(id));
      try {
        const res = await apiPut<{ ok: boolean; summary: string }>(
          `/scans/${encodeURIComponent(id)}/summary`,
          { summary },
        );
        const saved = res.summary ?? summary;
        savedRef.current[id] = saved;
        setDrafts((prev) => {
          if ((prev[id] ?? "").trim() !== value.trim()) return prev;
          return { ...prev, [id]: saved };
        });
        setItems((prev) =>
          prev ? prev.map((d) => (d.id === id ? { ...d, summary: saved || null } : d)) : prev,
        );
      } catch (e) {
        showToast("error", e instanceof Error ? e.message : "Failed to save summary");
      } finally {
        setSaving((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [showToast],
  );

  function setDraft(id: string, value: string) {
    setDrafts((prev) => ({ ...prev, [id]: value }));
    if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
    timersRef.current[id] = setTimeout(() => {
      void persistSummary(id, value);
    }, 500);
  }

  function flushSave(id: string) {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    void persistSummary(id, drafts[id] ?? "");
  }

  const persistLocation = useCallback(
    async (id: string, value: string) => {
      const locationCode = value.trim();
      const previously = (
        items?.find((d) => d.id === id)?.locationCode ??
        ""
      ).trim();
      if (locationCode === previously) {
        // No change — re-lock if it already had a value.
        if (previously) {
          setLocEditing((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
        return;
      }

      setLocSaving((prev) => new Set(prev).add(id));
      try {
        const res = await apiPut<{ ok: boolean; locationCode: string }>(
          `/scans/${encodeURIComponent(id)}/location`,
          { locationCode },
        );
        const saved = (res.locationCode ?? locationCode).trim();
        setLocDrafts((prev) => ({ ...prev, [id]: saved }));
        setItems((prev) =>
          prev
            ? prev.map((d) => (d.id === id ? { ...d, locationCode: saved || null } : d))
            : prev,
        );
        // Remember it so the next document suggests the incremented code.
        if (saved) setLastLocation(saved);
        // Lock after a successful save whenever non-empty (or even empty — locked empty
        // is awkward; only lock when there is a code).
        setLocEditing((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } catch (e) {
        showToast("error", e instanceof Error ? e.message : "Failed to save location code");
      } finally {
        setLocSaving((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [items, showToast],
  );

  function startLocEdit(id: string) {
    const current = items?.find((d) => d.id === id)?.locationCode ?? "";
    setLocDrafts((prev) => ({ ...prev, [id]: current }));
    setLocEditing((prev) => new Set(prev).add(id));
  }

  function cancelLocEdit(id: string) {
    const current = items?.find((d) => d.id === id)?.locationCode ?? "";
    setLocDrafts((prev) => ({ ...prev, [id]: current }));
    setLocEditing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function handleDelete(doc: ScanListItem) {
    setConfirmDelete(doc);
  }

  async function doDelete(doc: ScanListItem) {
    setDeleting((prev) => new Set(prev).add(doc.id));
    try {
      await apiDelete<{ ok: boolean; id: string }>(`/scans/${encodeURIComponent(doc.id)}`);
      setItems((prev) => (prev ? prev.filter((d) => d.id !== doc.id) : prev));
      showToast("success", "Document deleted");
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
      setConfirmDelete(null);
    }
  }

  async function handleAi(id: string) {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setAiBusy((prev) => new Set(prev).add(id));
    try {
      const detail = await apiGet<ScanDetail>(`/scans/${encodeURIComponent(id)}`);
      const corpus = (detail.ocrText || "").trim();
      if (!corpus) {
        showToast("error", "No OCR text on this document to summarize.");
        return;
      }
      const res = await apiPost<{ summary: string }>("/scans/summarize", { text: corpus });
      const next = (res.summary || "").trim();
      if (!next) {
        showToast("error", "AI returned an empty summary.");
        return;
      }
      setDrafts((prev) => ({ ...prev, [id]: next }));
      const saved = await apiPut<{ ok: boolean; summary: string }>(
        `/scans/${encodeURIComponent(id)}/summary`,
        { summary: next },
      );
      const final = saved.summary ?? next;
      savedRef.current[id] = final;
      setDrafts((prev) => ({ ...prev, [id]: final }));
      setItems((prev) =>
        prev ? prev.map((d) => (d.id === id ? { ...d, summary: final } : d)) : prev,
      );
      showToast("success", "AI summary saved");
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "AI summary failed");
    } finally {
      setAiBusy((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }


  const uploadsBlocked = isAll
    ? "“All” is a read-only view. Pick a document set to import into."
    : null;

  async function uploadFile(file: File) {
    if (!file) return;
    if (uploadsBlocked) {
      showToast("error", uploadsBlocked);
      return;
    }
    if (!documentSet) {
      showToast("error", "Select a document set in the sidebar first.");
      return;
    }
    const name = file.name || "document.bin";
    setUploadingPdf(true);
    try {
      const form = new FormData();
      form.append("file", file, name);
      const res = await apiUploadForm<{ stored: boolean; id: string; pageCount?: number }>(
        "/scans/upload-pdf",
        form,
      );
      if (!res.stored) {
        showToast("error", "Server did not store the file.");
        return;
      }
      const isPdf =
        name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
      showToast(
        "success",
        isPdf
          ? `Imported PDF (${res.pageCount ?? "?"} page${(res.pageCount ?? 0) === 1 ? "" : "s"})`
          : `Imported ${name}`,
      );
      load();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploadingPdf(false);
    }
  }

  function onDropFiles(files: FileList | File[] | null) {
    if (!files) return;
    if (uploadsBlocked) {
      showToast("error", uploadsBlocked);
      return;
    }
    const list = Array.from(files);
    if (list.length === 0) return;
    // Sequential import
    void (async () => {
      for (const f of list) {
        await uploadFile(f);
      }
    })();
  }

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((doc) => {
      const hay = [
        drafts[doc.id] ?? "",
        doc.summary ?? "",
        doc.locationCode ?? "",
        locDrafts[doc.id] ?? "",
        doc.ocrText ?? doc.preview ?? "",
        doc.uploadedBy ?? "",
        doc.setName ?? "",
        formatWhen(doc.createdAt),
        String(doc.pageCount),
        `${doc.pageCount} page`,
        `${doc.pageCount} pages`,
      ]
        .join("\n")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter, drafts, locDrafts]);

  /** Suggested next location code (increment of the most recent one). */
  const suggestedLocation = incrementLocation(lastLocation);

  return (
    <RequireAuth>
      <div
        className={
          "max-w-4xl mx-auto py-8 px-4 relative min-h-[60vh] rounded-lg transition-colors " +
          (dragOver ? "ring-2 ring-cyan bg-cyan/5" : "")
        }
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploadsBlocked) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploadsBlocked) setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // only clear when leaving the container
          if (e.currentTarget === e.target) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          onDropFiles(e.dataTransfer.files);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={(e) => {
            onDropFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {(dragOver || uploadingPdf) && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-surface/80 border-2 border-dashed border-cyan">
            <div className="text-center px-4">
              {uploadingPdf ? (
                <>
                  <Spinner className="w-8 h-8 mx-auto text-cyan mb-2" />
                  <p className="text-foreground font-medium m-0">Importing…</p>
                  <p className="text-muted text-sm m-0 mt-1">
                    PDFs are OCR&apos;d on the server; other files are stored as-is
                  </p>
                </>
              ) : (
                <>
                  <p className="text-foreground font-medium m-0 text-lg">Drop files to import</p>
                  <p className="text-muted text-sm m-0 mt-1">Any file type — PDFs get OCR</p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <h1 className={s.pageTitle + " mb-0 shrink-0"}>Documents</h1>
          {items && items.length > 0 ? (
            <div className="flex-1 min-w-0">
              <label htmlFor="doc-filter" className="sr-only">
                Filter documents
              </label>
              <input
                id="doc-filter"
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter documents…"
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-md border border-border bg-surface-light px-3 py-2 text-sm text-foreground placeholder-muted focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan"
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={s.btnAuxiliary + " shrink-0 !px-2.5"}
            disabled={uploadingPdf || uploadsBlocked !== null}
            title={uploadsBlocked ?? "Import files"}
            aria-label="Import files"
          >
            {uploadingPdf ? <Spinner className="w-4 h-4" /> : <ImportIcon className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={load}
            className={s.btnAuxiliary + " shrink-0 !px-2.5"}
            disabled={loading || uploadingPdf}
            title="Refresh"
            aria-label="Refresh"
          >
            {loading ? <Spinner className="w-4 h-4" /> : <RefreshIcon className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-muted -mt-2 mb-3">
          {isAll
            ? "Showing every document across all of your sets. This view is read-only — switch to a specific set to import."
            : "Drag and drop any file here (imports into the selected document set). PDFs are OCR'd on the server."}
        </p>
        {items && items.length > 0 && filter.trim() && (
          <p className="text-xs text-muted -mt-2 mb-3">
            {filtered?.length ?? 0} of {items.length} document
            {items.length === 1 ? "" : "s"}
          </p>
        )}

        {error && <p className="text-red mb-4">{error}</p>}
        {loading && !items && <p className="text-muted">Loading…</p>}
        {items && items.length === 0 && (
          <div className={s.card}>
            <p className="text-muted m-0">
              No documents yet. Capture a page on the QScanner iOS app and tap <strong>Send</strong> to
              upload.
            </p>
          </div>
        )}

        {items && items.length > 0 && filtered && filtered.length === 0 && (
          <div className={s.card}>
            <p className="text-muted m-0">
              No documents match <strong>{filter.trim()}</strong>.
            </p>
          </div>
        )}

        {filtered && filtered.length > 0 && (
          <ul className="space-y-2 list-none p-0 m-0">
            {filtered.map((doc) => {
              const isDeleting = deleting.has(doc.id);
              const isSaving = saving.has(doc.id);
              const isAi = aiBusy.has(doc.id);
              const savedLoc = (doc.locationCode ?? "").trim();
              const hasLocation = savedLoc.length > 0;
              const isLocEditing = !hasLocation || locEditing.has(doc.id);
              const isLocSaving = locSaving.has(doc.id);
              return (
                <li
                  key={doc.id}
                  className="rounded-lg border border-border bg-surface px-3 py-2.5"
                >
                  <div className="flex gap-3 items-start">
                    {/* Thumbnail → document preview (unchanged) */}
                    <Link
                      href={`/documents/${doc.id}`}
                      className="w-14 h-[4.5rem] shrink-0 rounded overflow-hidden bg-surface-light border border-border flex items-center justify-center no-underline hover:border-accent transition-colors"
                      title="Open preview"
                    >
                      {doc.thumbnail ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={`/api/scans/${doc.id}/files/${encodeURIComponent(doc.thumbnail)}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-muted text-[10px] px-1 text-center leading-tight">
                          No image
                        </span>
                      )}
                    </Link>

                    <div className="min-w-0 flex-1">
                      {/* Line 1: summary */}
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          id={`summary-${doc.id}`}
                          value={drafts[doc.id] ?? ""}
                          onChange={(e) => setDraft(doc.id, e.target.value)}
                          onBlur={() => flushSave(doc.id)}
                          maxLength={120}
                          placeholder="Summary…"
                          disabled={isAi}
                          className="flex-1 min-w-0 rounded-md border border-border bg-surface-light px-2.5 py-1.5 text-sm text-foreground placeholder-muted focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan"
                          aria-label="Document summary"
                        />
                        <button
                          type="button"
                          onClick={() => handleAi(doc.id)}
                          disabled={isAi || isSaving}
                          className={
                            s.btnAuxiliary +
                            " inline-flex items-center gap-1.5 shrink-0 !px-2.5 !py-1.5 text-xs"
                          }
                          title="Generate AI summary"
                          aria-label="Generate AI summary"
                        >
                          {isAi ? (
                            <Spinner className="w-3.5 h-3.5" />
                          ) : (
                            <SparklesIcon className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadScan(doc.id)}
                          className="text-muted hover:text-accent disabled:opacity-40 p-1.5 rounded-md shrink-0"
                          title="Open document"
                          aria-label="Open document"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(doc)}
                          disabled={isDeleting}
                          className="p-1.5 rounded-md shrink-0 hover:opacity-80 disabled:opacity-40"
                          title="Delete document"
                          aria-label="Delete document"
                        >
                          {isDeleting ? (
                            <Spinner className="w-4 h-4 text-red" />
                          ) : (
                            <TrashIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Line 2: pages · date · Location: code (right).
                          pl matches the summary input's text inset (1px border +
                          px-2.5) so this line aligns with the description text. */}
                      <div className="flex items-center gap-2 mt-1 pl-[11px] text-xs text-muted">
                        {isAll && doc.setName && (
                          <span
                            className="shrink-0 max-w-[10rem] truncate rounded bg-surface-light border border-border px-1.5 py-0.5 text-[10px] text-foreground/80"
                            title={`Document set: ${doc.setName}`}
                          >
                            {doc.setName}
                          </span>
                        )}
                        <span>
                          {doc.pageCount} page{doc.pageCount === 1 ? "" : "s"}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span>{formatWhen(doc.createdAt)}</span>
                        {isSaving && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="text-muted/80">Saving…</span>
                          </>
                        )}
                        <div className="flex-1 min-w-2" />
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-muted">Location:</span>
                          {isLocEditing ? (
                            <input
                              type="text"
                              id={`location-${doc.id}`}
                              value={locDrafts[doc.id] ?? ""}
                              onChange={(e) =>
                                setLocDrafts((prev) => ({ ...prev, [doc.id]: e.target.value }))
                              }
                              onBlur={() => void persistLocation(doc.id, locDrafts[doc.id] ?? "")}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  // Accept the suggested code when the field is
                                  // still empty; otherwise save what was typed.
                                  const cur = (locDrafts[doc.id] ?? "").trim();
                                  if (!cur && !hasLocation && suggestedLocation) {
                                    e.preventDefault();
                                    setLocDrafts((prev) => ({
                                      ...prev,
                                      [doc.id]: suggestedLocation,
                                    }));
                                    void persistLocation(doc.id, suggestedLocation);
                                  } else {
                                    e.currentTarget.blur();
                                  }
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  if (hasLocation) cancelLocEdit(doc.id);
                                }
                              }}
                              maxLength={64}
                              placeholder={
                                !hasLocation && suggestedLocation ? suggestedLocation : "…"
                              }
                              disabled={isLocSaving}
                              autoFocus={hasLocation && locEditing.has(doc.id)}
                              className="w-24 rounded border border-border bg-surface-light px-1.5 py-0 text-xs font-mono text-right text-foreground placeholder-muted focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan"
                              aria-label="Location code"
                            />
                          ) : (
                            <>
                              <span
                                className="font-mono text-foreground/90 truncate max-w-[8rem]"
                                title={savedLoc}
                              >
                                {savedLoc}
                              </span>
                              <button
                                type="button"
                                onClick={() => startLocEdit(doc.id)}
                                className="align-middle hover:opacity-80 p-0.5 shrink-0"
                                title="Edit location code"
                                aria-label="Edit location code"
                              >
                                <EditIcon className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          {isLocSaving && <span className="text-muted/80">…</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete document?"
        message={
          confirmDelete ? (
            <>
              <span className="text-foreground">
                {(drafts[confirmDelete.id] || confirmDelete.preview || confirmDelete.id).slice(0, 100)}
              </span>
              <br />
              This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete"
        destructive
        busy={confirmDelete ? deleting.has(confirmDelete.id) : false}
        onConfirm={() => {
          if (confirmDelete) void doDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </RequireAuth>
  );
}
